import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import Box from '@material-ui/core/Box'
import makeStyles from '@material-ui/core/styles/makeStyles'

import { getPlans } from 'checkout-module/checkout/api/billingApi'
import PlanBlock from 'checkout-module/checkout/ui/transmission-plans-page/PlanBlock'
import { canTheSubscriptionBeUpgraded } from 'checkout-module/checkout/ui/shared/canTheSubscriptionBeUpgraded'
import useSelectedCamera from 'camera-module/camera/core/useSelectedCamera'
import Spinner from 'shared-module/components/Spinner'
import { useECommerceProductsActions } from 'ecommerce-module/core/eCommerce.hook'
import eCommerceActions from 'ecommerce-module/core/eCommerce.actions'
import { useDispatch, useSelector } from 'react-redux'
import { getBasketSelector, getProductItemsSelector } from 'ecommerce-module/core/eCommerce.selectors'
import PlanMissingUi from './PlanMissingUi'
import SwitchToggle from 'shared-module/ui/switch-toggle/spypoint'
import { useTranslation } from 'react-i18next'
import { useUserClubMembership } from 'spypoint/src/user-module/club-membership/core/useUserClubMembership'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
  },
  blocks: {
    display: 'flex',
    flexWrap: 'wrap',
    flexGrow: '2',
    maxWidth: '1000px',
    justifyContent: 'center',
    alignItems: 'flex-end',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
    },
  },
  toggleMargin: {
    margin: '24px',
    [theme.breakpoints.down('xs')]: {
      margin: '44px 0px 0px 0px',
    },
  },
}))

const monthlyPeriodType = 'month_by_month'
const annualPeriodType = 'annual'

const PlanList = ({ eCommerceEnabled, handleSwitchToggleChange, switchToggleState, isStripeSubscription, subscription, planPeriod = 'month_by_month' }) => {
  const classes = useStyles()
  const history = useHistory()
  const camera = useSelectedCamera()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [plans, setPlans] = useState(null)
  const { getProducts } = useECommerceProductsActions()
  const { productItems } = useSelector(getProductItemsSelector)
  const userClubMembership = useUserClubMembership()
  const plansLoading = !Array.isArray(plans?.plans)
  const hasPlans =
    (Array.isArray(plans?.plans) && plans?.plans.length > 0)

  function checkDuplicatePlan (basket, productToAdd) {
    let isDuplicate = false
    if (!basket || basket.length === 0) { return isDuplicate }
    const planCodes = ['D06120', 'D06121', 'D06130', 'D06131', 'D06140', 'D06141']
    // Check if the product to add is a plan
    const isPlan = planCodes.includes(productToAdd[0].productId)

    if (isPlan) {
      // Iterate over the basket and check if any product has the same cameraId and is a plan
      for (let i = 0; i < basket.length; i++) {
        const basketProduct = basket[i]
        if (basketProduct.c_cameraId === productToAdd[0].cameraId && planCodes.includes(basketProduct.productId)) {
          isDuplicate = true
          break
        }
      }
    }

    return isDuplicate
  }

  const addProductToBasket = (product) => {
    const storedBasketId = localStorage.getItem('basketId')
    const duplicatedPlan = checkDuplicatePlan(productItems, product)
    if (duplicatedPlan) {
      return false
    } else {
      if (!storedBasketId || storedBasketId === 'undefined') {
        eCommerceActions.createBaskets().then(newBasket => dispatch(eCommerceActions.addItemsToBasket(newBasket?.basketId, [{ ...product[0], subscribeProId: subscription?.externalId, insiderclubMember: userClubMembership?.isMember }])))
      } else { dispatch(eCommerceActions.addItemsToBasket(storedBasketId, [{ ...product[0], subscribeProId: subscription?.externalId, insiderclubMember: userClubMembership?.isMember }])) }
    }
    return true
  }

  useEffect(() => {
    if (!eCommerceEnabled && !plans) {
      getPlans().then(plans => setPlans(plans))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchProducts = async () => {
      if (eCommerceEnabled) {
        const plans = await getProducts()
        setPlans(plans.products)
      }
    }
    fetchProducts()
  }, [eCommerceEnabled])

  const buildRedirectParams = plan => {
    const params = new URLSearchParams(window.location.search)
    params.append('p', plan.plan.id)
    params.append('f', plan.frequency)

    return params.toString()
  }

  const redirectToPaymentPage = plan => history.push('/camera/' + camera.id + '/subscribe?' + buildRedirectParams(plan))

  const renderBlocks = () => {
    const currentPlan = subscription.plan

    const setPlans = () => subscription?.plan.id === 'Free'
      ? [subscription.plan, ...plans?.plans?.filter(plan => plan.period === planPeriod)]
      : plans?.plans?.filter(plan => plan.period === planPeriod)

    const setLegacyPlans = () => {
      if (currentPlan.isActive) {
        return subscription?.plan.id === 'Free'
          ? plans.filter(plan => plan.isActive)
          : plans.filter((plan) => (plan.id === 'Basic' || plan.id === 'Standard' || plan.id === 'Premium'))
      } else return plans.filter(plan => plan.isActive && plan.isFree)
    }

    const activePlans = eCommerceEnabled ? setPlans() : setLegacyPlans()

    return (

      <Box>
        <Box align="center" className={classes.toggleMargin}>
          <SwitchToggle
            onChange={handleSwitchToggleChange}
            switchToggleState={switchToggleState}
            firstOption={t('app:photo_transmition_plan.billing.monthly')}
            secondOption={t('app:photo_transmition_plan.billing.annually')}
          />
        </Box>
        <div className={classes.blocks}>
          { activePlans.map(plan => {
            plan.canUpgradeMonthByMonth = canTheSubscriptionBeUpgraded(subscription, plan, monthlyPeriodType, eCommerceEnabled)
            plan.canUpgradeAnnual = canTheSubscriptionBeUpgraded(subscription, plan, annualPeriodType, eCommerceEnabled)

            return <PlanBlock isStripeSubscription={isStripeSubscription} eCommerceEnabled={eCommerceEnabled} key={plan.id} plan={plan} subscription={subscription} periodType={planPeriod} onSelectPlan={redirectToPaymentPage} addProductToBasket={addProductToBasket} />
          }) }
        </div>
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      { eCommerceEnabled
        ? (
            plansLoading
              ? (
                <Spinner />
                )
              : subscription && hasPlans
                ? (
                    renderBlocks()
                  )
                : (
                  <PlanMissingUi />
                  )
          )
        : subscription && plans
          ? (
              renderBlocks()
            )
          : (
            <Box mt={2} mb={4}>
              <Spinner />
            </Box>
            ) }
    </Box>
  )
}

export default PlanList
